import { useTenantQuery } from '@/hooks/useTenantQuery';

interface LicenseStatusResponse {
  status: string | null;
  trial_expires_at: string | null;
  days_remaining: number | null;
}

export interface TrialStatus {
  /** Tenant's license is a self-serve trial */
  isTrial: boolean;
  /** Trial exists and its clock has run out (backend blocks writes) */
  isExpired: boolean;
  /** Whole days left on the trial (0 when expired); null for non-trial tenants */
  daysRemaining: number | null;
  trialExpiresAt: Date | null;
  /** UX nicety only — the backend 403 TRIAL_EXPIRED is the real gate */
  canWrite: boolean;
}

/**
 * Trial state for the active tenant, from GET /api/licensing/my-status.
 * Non-trial tenants (no license row, or charter/active/expired/suspended)
 * return isTrial=false and the banner renders nothing.
 */
export function useTrialStatus(): TrialStatus {
  const { data } = useTenantQuery<LicenseStatusResponse>(
    ['license-status'],
    '/licensing/my-status',
    { staleTime: 5 * 60 * 1000 }
  );

  const isTrial = data?.status === 'trial';
  const trialExpiresAt = data?.trial_expires_at ? new Date(data.trial_expires_at) : null;
  const isExpired = isTrial && !!trialExpiresAt && trialExpiresAt.getTime() <= Date.now();
  const daysRemaining =
    isTrial && trialExpiresAt
      ? Math.max(0, Math.ceil((trialExpiresAt.getTime() - Date.now()) / 86_400_000))
      : null;

  return { isTrial, isExpired, daysRemaining, trialExpiresAt, canWrite: !isExpired };
}
